import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Reminders from "@/pages/Reminders";
import Settings from "@/pages/Settings";
import History from "@/pages/History";
import Templates from "@/pages/Templates";
import Invoices from "@/pages/Invoices";
import Layout from "@/components/app/Layout";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminBusinesses from "@/pages/admin/AdminBusinesses";
import AdminBusinessDetail from "@/pages/admin/AdminBusinessDetail";
import AdminRankings from "@/pages/admin/AdminRankings";
import AdminReports from "@/pages/admin/AdminReports";
import AdminSearch from "@/pages/admin/AdminSearch";
import AdminDistributors from "@/pages/admin/AdminDistributors";
import MarketingLayout from "@/pages/marketing/MarketingLayout";
import Home from "@/pages/marketing/Home";
import Features from "@/pages/marketing/Features";
import Pricing from "@/pages/marketing/Pricing";
import About from "@/pages/marketing/About";
import Contact from "@/pages/marketing/Contact";
import Privacy from "@/pages/marketing/Privacy";
import Terms from "@/pages/marketing/Terms";
import Refund from "@/pages/marketing/Refund";
import DataDeletion from "@/pages/marketing/DataDeletion";
import { Toaster } from "sonner";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="font-heading text-2xl font-bold animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

const Public = ({ children }) => <MarketingLayout>{children}</MarketingLayout>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public marketing pages */}
      <Route path="/" element={<Public><Home /></Public>} />
      <Route path="/features" element={<Public><Features /></Public>} />
      <Route path="/pricing" element={<Public><Pricing /></Public>} />
      <Route path="/about" element={<Public><About /></Public>} />
      <Route path="/contact" element={<Public><Contact /></Public>} />
      <Route path="/privacy-policy" element={<Public><Privacy /></Public>} />
      <Route path="/terms-and-conditions" element={<Public><Terms /></Public>} />
      <Route path="/refund-and-cancellation" element={<Public><Refund /></Public>} />
      <Route path="/data-deletion" element={<Public><DataDeletion /></Public>} />
      {/* Legacy redirects to new canonical URLs */}
      <Route path="/privacy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
      <Route path="/refund" element={<Navigate to="/refund-and-cancellation" replace />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />

      {/* Protected app */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

      {/* Owner-only admin console. Backend enforces 403 on non-owners. */}
      <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminOverview />} />
        <Route path="businesses" element={<AdminBusinesses />} />
        <Route path="businesses/:user_id" element={<AdminBusinessDetail />} />
        <Route path="rankings" element={<AdminRankings />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="search" element={<AdminSearch />} />
        <Route path="distributors" element={<AdminDistributors />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
